import React, { useState, useEffect } from 'react';

import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import PageHeader from '@shared/PageHeader';
import CreateInboundModal from '@shared/purchaseOrders/CreateInboundModal';
import POAttachments from '@shared/purchaseOrders/POAttachments';
import POComments from '@shared/purchaseOrders/POComments';
import POHistory from '@shared/purchaseOrders/POHistory';
import POInvoicesPanel from '@shared/purchaseOrders/POInvoicesPanel';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import { formatDateTime, formatDate } from '@utils/dateTime';
import {
  Card,
  Descriptions,
  Table,
  Tag,
  Button,
  Space,
  Modal,
  Spin,
  message,
  Input,
  Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link, useRevalidator } from 'react-router-dom';

const STATUS_COLORS = {
  draft: 'default',
  submitted: 'orange',
  approved: 'green',
  partial_received: 'blue',
  received: 'cyan',
  closed: 'default',
  canceled: 'red',
};

const INBOUND_STATUS_COLORS = {
  pending: 'blue',
  verified: 'cyan',
  putting_away: 'orange',
  done: 'green',
  canceled: 'red',
};

/**
 * 通用 PurchaseOrder 顯示視圖組件
 *
 * @param {Object} props
 * @param {Object} props.purchaseOrder - 採購單資料（來自 loader）
 * @param {Object} props.service - purchaseOrderService (admin 或 operator)
 * @param {Object} [props.warehouseService] - warehouseService (operator 使用)
 * @param {Array} [props.warehouses] - 倉庫列表（admin 從 MerchantContext 傳入）
 * @param {string} props.role - 角色 ('admin' 或 'operator')
 * @param {boolean} props.canApprove - 是否有核准權限
 */
export default function PurchaseOrderShow({
  purchaseOrder: initialPO,
  service,
  warehouseService,
  warehouses: warehousesProp,
  role = 'admin',
  canApprove = false,
  currentUserId,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const revalidator = useRevalidator();

  const [po, setPo] = useState(initialPO);
  const [createInboundModalOpen, setCreateInboundModalOpen] = useState(false);
  const [warehouses, setWarehouses] = useState(warehousesProp || []);
  const [actionLoading, setActionLoading] = useState(false);
  const [timelineData, setTimelineData] = useState([]);

  const isRevalidating = revalidator.state === 'loading';
  const comments = timelineData.filter((item) => item.type === 'comment');
  const histories = timelineData.filter((item) => item.type === 'history');

  // Sync loader data changes
  useEffect(() => {
    if (initialPO) {
      setPo(initialPO);
    }
  }, [initialPO]);

  // Sync warehouses prop (from MerchantContext)
  useEffect(() => {
    if (warehousesProp) {
      setWarehouses(warehousesProp);
    }
  }, [warehousesProp]);

  // Load warehouses for CreateInboundModal (fallback when no prop provided)
  useEffect(() => {
    if (warehousesProp || !warehouseService) return;
    const loadWarehouses = async () => {
      try {
        const res = await warehouseService.fetchList();
        if (res.success && res.data) {
          setWarehouses(res.data);
        }
      } catch (error) {
        console.error('Failed to load warehouses:', error);
      }
    };
    loadWarehouses();
  }, [warehouseService, warehousesProp]);

  // Load timeline (comments + histories)
  const loadTimeline = async () => {
    try {
      const res = await service.getTimeline(po?.id);
      if (res.success) {
        setTimelineData(res.data || []);
      }
    } catch (error) {
      console.error('Failed to load timeline:', error);
    }
  };

  useEffect(() => {
    if (po?.id) loadTimeline();
  }, [po?.id]);

  // === Action handlers ===

  const handleAction = async (actionFn, ...args) => {
    try {
      setActionLoading(true);
      const response = await actionFn(...args);
      if (response.success) {
        message.success(t('common.submit.success'));
        revalidator.revalidate();
      } else {
        handleApiError(response, { t, fallbackMessage: t('common.error.operation_failed') });
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Action error:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmit = () => handleAction(() => service.submit(po.id));
  const handleApprove = () => handleAction(() => service.approve(po.id));

  const confirmAction = ({ titleKey, contentKey, danger, action }) => {
    Modal.confirm({
      title: t(titleKey),
      content: t(contentKey, { name: po.name }),
      okText: t('common.button.confirm'),
      cancelText: t('common.button.cancel'),
      okButtonProps: danger ? { danger: true } : undefined,
      onOk: action,
    });
  };

  const handleReject = () => confirmAction({
    titleKey: 'shared.purchase_orders.show.reject_confirm_title',
    contentKey: 'shared.purchase_orders.show.reject_confirm_content',
    danger: true,
    action: () => handleAction(() => service.reject(po.id)),
  });
  const handleCancel = () => confirmAction({
    titleKey: 'shared.purchase_orders.show.cancel_confirm_title',
    contentKey: 'shared.purchase_orders.show.cancel_confirm_content',
    danger: true,
    action: () => handleAction(() => service.cancel(po.id)),
  });
  const handleClose = () => confirmAction({
    titleKey: 'shared.purchase_orders.show.close_confirm_title',
    contentKey: 'shared.purchase_orders.show.close_confirm_content',
    action: () => handleAction(() => service.close(po.id)),
  });

  const handleShortClose = () => {
    let closeReason = '';
    Modal.confirm({
      title: t('shared.purchase_orders.show.short_close_confirm_title'),
      content: (
        <div style={{ marginTop: 12 }}>
          <div style={{ marginBottom: 8 }}>{t('shared.purchase_orders.show.short_close_confirm_content')}</div>
          <Input.TextArea
            rows={3}
            placeholder={t('shared.purchase_orders.show.short_close_reason_placeholder')}
            onChange={(e) => { closeReason = e.target.value; }}
          />
        </div>
      ),
      okText: t('common.button.confirm'),
      cancelText: t('common.button.cancel'),
      onOk: () => handleAction(() => service.shortClose(po.id, closeReason)),
    });
  };

  const handleCreateInboundSuccess = () => {
    revalidator.revalidate();
  };

  const basePath = role === 'operator' ? '/operator' : '/admin';

  // === Action buttons based on status ===

  const renderActionButtons = () => {
    const { status } = po;
    const buttons = [];
    const hasRemainingItems = po.items?.some((item) => item.remaining_quantity > 0);

    if (status === 'draft') {
      buttons.push(
        <Button
          key="edit"
          icon={<EditOutlined />}
          onClick={() => navigate(`/purchase_orders/${po.id}/edit`)}
        >
          {t('common.button.edit')}
        </Button>
      );
      buttons.push(
        <Button
          key="submit"
          type="primary"
          icon={<CheckOutlined />}
          onClick={handleSubmit}
          loading={actionLoading}
        >
          {t('common.button.submit')}
        </Button>
      );
      buttons.push(
        <Button
          key="cancel"
          danger
          icon={<StopOutlined />}
          onClick={handleCancel}
          loading={actionLoading}
        >
          {t('common.button.cancel')}
        </Button>
      );
    }

    if (status === 'submitted') {
      if (canApprove) {
        buttons.push(
          <Button
            key="approve"
            type="primary"
            icon={<CheckOutlined />}
            onClick={handleApprove}
            loading={actionLoading}
          >
            {t('common.button.approve')}
          </Button>
        );
        buttons.push(
          <Button
            key="reject"
            icon={<CloseOutlined />}
            onClick={handleReject}
            loading={actionLoading}
          >
            {t('common.button.reject')}
          </Button>
        );
      }
      buttons.push(
        <Button
          key="cancel"
          danger
          icon={<StopOutlined />}
          onClick={handleCancel}
          loading={actionLoading}
        >
          {t('common.button.cancel')}
        </Button>
      );
    }

    if (status === 'approved') {
      if (hasRemainingItems) {
        buttons.push(
          <Button
            key="create_inbound"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateInboundModalOpen(true)}
          >
            {t('shared.purchase_orders.show.create_inbound')}
          </Button>
        );
      }
      buttons.push(
        <Button
          key="cancel"
          danger
          icon={<StopOutlined />}
          onClick={handleCancel}
          loading={actionLoading}
        >
          {t('common.button.cancel')}
        </Button>
      );
    }

    if (status === 'partial_received') {
      if (hasRemainingItems) {
        buttons.push(
          <Button
            key="create_inbound"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateInboundModalOpen(true)}
          >
            {t('shared.purchase_orders.show.create_inbound')}
          </Button>
        );
      }
      buttons.push(
        <Button
          key="short_close"
          danger
          onClick={handleShortClose}
          loading={actionLoading}
        >
          {t('shared.purchase_orders.show.short_close')}
        </Button>
      );
    }

    if (status === 'received') {
      buttons.push(
        <Button
          key="close"
          type="primary"
          icon={<CheckOutlined />}
          onClick={handleClose}
          loading={actionLoading}
        >
          {t('common.button.close')}
        </Button>
      );
    }

    return buttons.length > 0 ? <Space wrap>{buttons}</Space> : null;
  };

  // === Items table columns ===

  const itemColumns = [
    {
      title: t('models.purchase_order_item.product_sku'),
      dataIndex: 'product_sku',
      key: 'product_sku',
      render: (sku, record) => (
        <Link to={`/products/${record.product_id}`} style={{ color: '#1890ff' }}>
          {sku}
        </Link>
      ),
    },
    {
      title: t('models.purchase_order_item.product'),
      dataIndex: 'product_name',
      key: 'product_name',
    },
    {
      title: t('models.purchase_order_item.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
    },
    {
      title: t('models.purchase_order_item.unit_price'),
      dataIndex: 'unit_price',
      key: 'unit_price',
      align: 'right',
      render: (val) => val != null ? parseFloat(val).toLocaleString() : '-',
    },
    {
      title: t('models.purchase_order_item.amount'),
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (val) => val != null ? parseFloat(val).toLocaleString() : '-',
    },
    {
      title: t('models.purchase_order_item.scheduled_quantity'),
      dataIndex: 'scheduled_quantity',
      key: 'scheduled_quantity',
      align: 'right',
      render: (qty, record) => {
        const scheduled = qty || 0;
        const ordered = record.quantity || 0;
        let color = '#999';
        if (scheduled === 0) color = '#999';
        else if (scheduled >= ordered) color = '#52c41a';
        else if (scheduled > 0) color = '#faad14';
        return <span style={{ color }}>{scheduled}</span>;
      },
    },
    {
      title: t('models.purchase_order_item.received_quantity'),
      dataIndex: 'received_quantity',
      key: 'received_quantity',
      align: 'right',
      render: (qty, record) => {
        const received = qty || 0;
        const ordered = record.quantity || 0;
        let color = '#999';
        if (received === 0) color = '#999';
        else if (received >= ordered) color = '#52c41a';
        else if (received >= ordered * 0.5) color = '#faad14';
        else color = '#ff4d4f';
        return <span style={{ color }}>{received}</span>;
      },
    },
    {
      title: t('models.purchase_order_item.remaining_quantity'),
      dataIndex: 'remaining_quantity',
      key: 'remaining_quantity',
      align: 'right',
      render: (qty) => {
        const remaining = qty || 0;
        const color = remaining > 0 ? '#faad14' : '#52c41a';
        return <span style={{ color }}>{remaining}</span>;
      },
    },
    {
      title: t('models.purchase_order_item.note'),
      dataIndex: 'note',
      key: 'note',
    },
  ];

  // === Inbounds compact render ===

  const renderInbounds = () => {
    if (!po?.inbounds?.length) return '-';
    return (
      <Space size={[8, 6]} wrap>
        {po.inbounds.map((inbound) => (
          <span key={inbound.id}>
            <Link to={`/inbounds/${inbound.id}`} style={{ marginRight: 4 }}>
              {inbound.name}
            </Link>
            <Tag color={INBOUND_STATUS_COLORS[inbound.status] || 'default'}>
              {t(`enums.inbound.status.${inbound.status}`, { defaultValue: inbound.status })}
            </Tag>
          </span>
        ))}
      </Space>
    );
  };

  // === Items summary row ===

  const itemsSummary = () => {
    if (!po?.items?.length) return null;
    const totalOrdered = po.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const totalReceived = po.items.reduce((sum, item) => sum + (item.received_quantity || 0), 0);

    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={2}>
            <strong>{t('shared.purchase_orders.show.total')}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={2} align="right">
            <strong>{totalOrdered}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={3} />
          <Table.Summary.Cell index={4} />
          <Table.Summary.Cell index={5} />
          <Table.Summary.Cell index={6} align="right">
            <strong>{totalReceived}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={7} />
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  // === Format helpers ===

  const formatTaxRate = (rate) => {
    if (rate == null) return '-';
    return `${(parseFloat(rate) * 100).toFixed(0)}%`;
  };

  const formatCurrencyAmount = (amount) => {
    if (amount == null) return '-';
    const symbol = po.currency || '';
    return `${symbol} ${parseFloat(amount).toLocaleString()}`;
  };

  if (!po) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        basePath={basePath}
        items={[
          'purchase_orders',
          'purchase_orders.list',
          { label: po.name },
        ]}
        extra={renderActionButtons()}
      />

      <Spin spinning={isRevalidating}>
        <div style={{ display: 'flex', gap: 16 }}>
          {/* Left: all content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* PO Info */}
            <Descriptions
              bordered
              column={2}
              size="small"
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label={t('models.purchase_order.name')}>
                {po.name}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.purchase_order.status')}>
                <Tag color={STATUS_COLORS[po.status] || 'default'}>
                  {t(`enums.purchase_order.status.${po.status}`, { defaultValue: po.status })}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('models.purchase_order.supplier')}>
                {po.supplier_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.purchase_order.currency')}>
                {po.currency || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.purchase_order.tax_rate')}>
                {formatTaxRate(po.tax_rate)}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.purchase_order.tax_included')}>
                {po.tax_included ? t('common.yes') : t('common.no')}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.purchase_order.expected_delivery_date')}>
                {po.expected_delivery_date ? formatDate(po.expected_delivery_date) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.purchase_order.subtotal')}>
                {formatCurrencyAmount(po.subtotal)}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <Space size={4}>
                    {t('models.purchase_order.tax_amount')}
                    <Tooltip
                      overlayInnerStyle={{ maxWidth: 'min(92vw, 440px)', width: 'max-content' }}
                      title={
                        <span style={{ whiteSpace: 'pre-line', lineHeight: 1.7 }}>
                          {t('shared.purchase_orders.form.tax_amount_hint')}
                        </span>
                      }
                    >
                      <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
                    </Tooltip>
                  </Space>
                }
              >
                {formatCurrencyAmount(po.tax_amount)}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.purchase_order.total_amount')}>
                <strong>{formatCurrencyAmount(po.total_amount)}</strong>
              </Descriptions.Item>
              <Descriptions.Item label={t('models.purchase_order.tabs.inbounds')} span={2}>
                {renderInbounds()}
              </Descriptions.Item>
              {po.close_reason && (
                <Descriptions.Item label={t('models.purchase_order.close_reason')} span={2}>
                  {po.close_reason}
                </Descriptions.Item>
              )}
            </Descriptions>

            {/* Comments */}
            <POComments
              comments={comments}
              poId={po?.id}
              service={service}
              currentUserId={currentUserId}
              onUpdate={loadTimeline}
            />

            {/* Items */}
            <Card title={t('models.purchase_order.tabs.items')} size="small" style={{ marginBottom: 16 }}>
              <Table
                rowKey="id"
                columns={itemColumns}
                dataSource={po?.items || []}
                pagination={false}
                size="small"
                summary={itemsSummary}
              />
            </Card>

            {/* Invoices */}
            <Card title={t('models.purchase_order.tabs.invoices')} size="small">
              <POInvoicesPanel po={po} service={service} />
            </Card>
          </div>

          {/* Right: Attachments + History */}
          <div style={{ width: 300, minWidth: 300 }}>
            <Card
              title={t('models.purchase_order.sidebar.attachments')}
              size="small"
              style={{ marginBottom: 16 }}
            >
              <POAttachments
                attachments={po?.attachments}
                poId={po?.id}
                service={service}
                onUpdate={() => { revalidator.revalidate(); loadTimeline(); }}
              />
            </Card>
            <POHistory histories={histories} />
          </div>
        </div>
      </Spin>

      {/* Create Inbound Modal */}
      <CreateInboundModal
        open={createInboundModalOpen}
        onClose={() => setCreateInboundModalOpen(false)}
        purchaseOrder={po}
        service={service}
        onSuccess={handleCreateInboundSuccess}
        warehouses={warehouses}
      />
    </div>
  );
}
